import { filterValuesSeparatorSplitRegex } from 'shared-helpers/consts';

import type { Router } from 'next/router';
import type { Locales } from 'shared-types';
import type {
  GetCompetenciesRes,
  GetCompetencyProfilesRes
} from 'shared-types/responses';

export type CompetencyInMatrix = {
  competencies?: GetCompetencyProfilesRes['competencyProfiles'][0]['competencies'];
  levels?: Record<'label' | 'value', string>[];
  level?: null | number;
  levelTitle?: string;
  title: string;
  value: number;
};

type LogicalIndexer = { [key: string]: true };

interface Params {
  competencies: GetCompetenciesRes;
  language: Locales;
  profiles: GetCompetencyProfilesRes;
  query: Router['query'];
}

const getCompetencyMatrixColumnsByView = ({
  competencies,
  language,
  profiles,
  query
}: Params) => {
  const getValidFilters = ({ profileOnly = false, noLevels = false }) => {
    const competencesInQueryById = ((query?.competencesInColumns as string)
      ?.split(filterValuesSeparatorSplitRegex)
      .reduce((competences: LogicalIndexer, competenceId: string) => {
        const [competenceIdStr, competenceLevelStr] = competenceId.split('.');
        if (noLevels) {
          competences[competenceIdStr] = true;
        } else if (competenceLevelStr && competenceLevelStr !== 'all') {
          const competency = competencies?.competencyObjs?.find(
            (comp) => `${comp.id}` === competenceIdStr
          );
          const level = competency?.levels?.find(
            (lvl) => `${lvl.id}` === competenceLevelStr
          );

          const idString = level
            ? `${competenceIdStr}.${level.level}`
            : `${competenceIdStr}.all`;
          competences[idString] = true;
        } else {
          competences[competenceId] = true;
        }

        return competences;
      }, {} as LogicalIndexer) || {}) as LogicalIndexer;

    const profilesInQueryById = ((query?.profilesInColumns as string)
      ?.split(filterValuesSeparatorSplitRegex)
      .reduce((profiles: LogicalIndexer, profileId: string) => {
        profiles[profileId] = true;
        return profiles;
      }, {} as LogicalIndexer) || {}) as LogicalIndexer;

    if (profileOnly) {
      const profileWithCompetences = competencesInQueryById
        ? profiles?.competencyProfiles
            ?.filter((profile) =>
              profile.competencies.some(
                (competence) =>
                  competencesInQueryById[`${competence.id}.all`] ||
                  competencesInQueryById[
                    `${competence.id}${
                      competence.level ? `.${competence.level.level}` : ''
                    }`
                  ]
              )
            )
            .reduce((profiles, profile) => {
              profiles[profile.id] = true;
              return profiles;
            }, {} as { [key: string]: true })
        : {};

      const relevantProfiles = {
        ...(profilesInQueryById || {}),
        ...profileWithCompetences
      };

      return Object.keys(relevantProfiles).length
        ? relevantProfiles
        : Object.keys(competencesInQueryById).length
        ? {}
        : null;
    }

    const competencesInProfiles = profilesInQueryById
      ? profiles?.competencyProfiles
          ?.filter((profile) => profilesInQueryById[profile.id])
          .reduce((competences, profile) => {
            profile.competencies.forEach((competence) => {
              const objId = noLevels
                ? competence.id
                : `${competence.id}${
                    competence.level ? `.${competence.level.level}` : ''
                  }`;

              competences[objId] = true;
            });
            return competences;
          }, {} as { [key: string]: true })
      : {};

    const relevantCompetences = {
      ...(competencesInQueryById || {}),
      ...competencesInProfiles
    };

    return Object.keys(relevantCompetences)?.length
      ? relevantCompetences
      : null;
  };

  const columnsInCompetenciesView = (): CompetencyInMatrix[] => {
    if (!competencies?.competencyObjs?.length) return [];
    const competencesById = getValidFilters({ noLevels: true });

    return competencies.competencyObjs
      .map((competency) => ({
        levels: competency.levels?.length
          ? competency.levels
              .sort((a, b) => a.level - b.level)
              .map((level) => ({
                label: level.nameLocales?.[language] || level.name,
                value: `${competency.id}.${level.level}`
              }))
          : [],
        title: competency.nameLocales?.[language] || competency.name,
        value: competency.id
      }))
      .flat()
      .filter(
        (competence) =>
          !competencesById || competencesById[`${competence.value}`]
      );
  };

  const columnsInCompetencyLevelsView = (): CompetencyInMatrix[] => {
    if (!competencies?.competencyObjs?.length) return [];
    const competencesById = getValidFilters({});

    return competencies.competencyObjs
      .map((competency) => {
        const level: number | null = null;
        const levelTitle = '';
        const title = competency.nameLocales?.[language] || competency.name;
        const value = competency.id;

        if (competency.levels?.length > 0) {
          return competency.levels.map((level) => ({
            level: level.level,
            levelTitle: level.nameLocales?.[language] || level.name,
            title,
            value
          }));
        }

        return { competencies: [], level, levelTitle, title, value };
      })
      .flat()
      .filter(
        (competence) =>
          !competencesById ||
          competencesById[`${competence.value}.all`] ||
          competencesById[
            `${competence.value}${
              competence.level ? `.${competence.level}` : ''
            }`
          ]
      );
  };

  const columnsInProfilesView = (): CompetencyInMatrix[] => {
    if (!profiles?.competencyProfiles?.length) return [];
    const profilesById = getValidFilters({ profileOnly: true });

    return profiles.competencyProfiles
      .filter((profile) => !profilesById || profilesById[profile.id])
      .map((profile) => ({
        competencies: profile.competencies,
        title: profile.titleLocales?.[language] || profile.title,
        value: profile.id
      }));
  };

  return query?.view === 'profiles'
    ? columnsInProfilesView()
    : query?.view === 'competency-levels'
    ? columnsInCompetencyLevelsView()
    : columnsInCompetenciesView();
};

export default getCompetencyMatrixColumnsByView;
